/**
 * `#/elegir-contrasena` — la contraseña propia. Etapa `R2-UI5`.
 *
 * PRD_R2 §«Elegir contraseña» · `R2-F6-CA-11..15`, `-18`, `-21`.
 *
 * ⛔ **Una sola pantalla para el primer ingreso Y para la reposición**
 * (PRD:2109, Regla 9). No hay variante: quien repuso su contraseña llega acá con
 * la misma marca puesta y lee lo mismo.
 *
 * ⛔ **Sin marco**, por lo mismo que `#/login`: la Regla 6 dice que hasta que
 * elija no ve ninguna otra pantalla.
 */

import { useState } from "react"
import type { JSX } from "react"
import {
  MINIMO_DE_CONTRASENA,
  crearBordeSesion,
  largoEnPuntosDeCodigo,
  type BordeSesion,
} from "./borde-sesion.ts"

export interface PropsPantallaElegirContrasena {
  /** Raíz de `auth.<sitio>`. La pone el marco. */
  readonly raiz: string
  /** Dónde cae cada rol — lo resuelve `R2-UI6`, no esta etapa. */
  readonly destinoPorRol?: (rol: string) => string
  /** Cómo se cambia de dirección. Del marco. */
  readonly navegar?: (destino: string) => void
  /** Sólo para el examen de componente. */
  readonly borde?: BordeSesion
}

export function PantallaElegirContrasena({
  raiz,
  destinoPorRol,
  navegar,
  borde,
}: PropsPantallaElegirContrasena): JSX.Element {
  const cable = borde ?? crearBordeSesion(raiz)

  const [nueva, setNueva] = useState("")
  const [repetir, setRepetir] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [enVuelo, setEnVuelo] = useState(false)

  const enviar = async (): Promise<void> => {
    if (enVuelo) return

    /*
      ⛔ El mínimo se cuenta en PUNTOS DE CÓDIGO (`[...clave].length`), no con
      `.length`, que cuenta unidades UTF-16. Con `.length` una contraseña de
      cuatro emojis «mide» ocho y entra: la Regla 7 valdría sólo para el alfabeto
      latino.

      ⛔ Y **ninguna otra exigencia**: ni mayúsculas, ni números, ni símbolos. El
      propio DNI como contraseña definitiva es un caso borde ASUMIDO (PRD:2263) y
      `R2-F6-CA-21` lo mide; agregar una regla «por seguridad» lo pone rojo.
    */
    if (largoEnPuntosDeCodigo(nueva) < MINIMO_DE_CONTRASENA) {
      setError(`La contraseña tiene que tener al menos ${MINIMO_DE_CONTRASENA} caracteres.`)
      return
    }
    /*
      La comparación de los dos campos vive ACÁ y no en el servidor: el contrato
      manda un solo campo (⟦elegir-no-es-change-password⟧), porque mandar los dos
      obligaría al servidor a decidir qué hacer cuando difieren — una pregunta que
      el PRD no le hace.
    */
    if (nueva !== repetir) {
      setError("Las dos contraseñas no coinciden.")
      return
    }

    setEnVuelo(true)
    setError(null)
    try {
      const resultado = await cable.elegirContrasena(nueva)
      if (!resultado.entro) {
        setError(resultado.mensaje)
        return
      }
      let rol = ""
      try {
        rol = (await cable.quienSoy())?.role ?? ""
      } catch {
        /* el marco lo resuelve al montar */
      }
      navegar?.(destinoPorRol?.(rol) ?? "")
    } finally {
      setEnVuelo(false)
    }
  }

  return (
    <main className="login">
      <h1 className="login__titulo">Elige tu contraseña</h1>
      <p className="login__bajada">
        Antes de entrar, elige una contraseña nueva. La que usaste para entrar deja de servir.
      </p>

      {error === null ? null : (
        <p className="login__rechazo" role="alert">
          {error}
        </p>
      )}

      <form
        className="login__formulario"
        onSubmit={(evento) => {
          evento.preventDefault()
          void enviar()
        }}
      >
        <div className="login__campo">
          <label htmlFor="elegir-nueva">Contraseña nueva</label>
          <input
            id="elegir-nueva"
            type="password"
            value={nueva}
            autoComplete="new-password"
            onChange={(evento) => {
              setNueva(evento.target.value)
            }}
          />
        </div>

        <div className="login__campo">
          <label htmlFor="elegir-repetir">Repetir contraseña nueva</label>
          <input
            id="elegir-repetir"
            type="password"
            value={repetir}
            autoComplete="new-password"
            onChange={(evento) => {
              setRepetir(evento.target.value)
            }}
          />
          <p className="login__ayuda">
            Al menos {MINIMO_DE_CONTRASENA} caracteres. No se piden mayúsculas, ni números, ni símbolos.
          </p>
        </div>

        {/* Por lo mismo que en `pantalla-login.tsx`: la acción cuelga del botón. */}
        <button
          className="boton boton--primario login__entrar"
          type="submit"
          disabled={enVuelo}
          onClick={(evento) => {
            evento.preventDefault()
            void enviar()
          }}
        >
          Guardar y entrar
        </button>
      </form>
    </main>
  )
}
